import React, { ReactNode, useEffect, useState } from 'react';
import {
  DateDetails,
  DateSectionClearanceData,
  MoreDetails,
  MoreStationDetails,
  LTSContextType,
  StationDetails,
  TrainDetail,
  TrainStatusDetail,
  UpcomingJourneyCardDetails
} from '../types/LTSTypes';
import { getDateDetails, getLTSDateListWithDaysOfRun } from '../utils/LTSUtils';
import { isEmpty } from '../../../utils/isEmpty';
import {
  ASYNC_STORAGE_KEYS,
  RADIO_OPTION_TRAIN_START_DATE,
  LABELS,
  PRIORITY_OF_DEFAULT_DATE_INDEX_ARRAY
} from '../constants/LTSConstants';
import { fetchStationList, fetchUpcomingJourneysList } from '../utils/ApiHandlers';
import { getDataFromLocalStorage, setDataInLocalStorage } from '../utils/LocalStorage';

export const LTSContext = React.createContext({} as LTSContextType);

const LTSContextProvider = ({ children }: { children: ReactNode }) => {
  // ---------- state ----------
  const [recentSearchTrains, setRecentSearchTrains] = useState<TrainDetail[]>([]);

  // ----------
  const [trainDetail, setTrainDetail] = useState({} as TrainDetail);
  const [boardingStation, setBoardingStation] = useState({} as StationDetails | undefined);
  const [dateDetail, setDateDetail] = useState({} as DateDetails | undefined);
  const [daysOfRun, setDaysOfRun] = useState([] as (boolean | undefined)[]);
  const [totalDaysOfRun, setTotalDaysOfRun] = useState(1);
  const [stationsList, setStationsList] = useState([] as MoreStationDetails[]);
  const [dateSectionData, setDateSectionData] = useState<DateSectionClearanceData>({
    clearance: true,
    startDateOptionSelected: true
  });

  // ----------
  const [radioValue, setRadioValue] = useState(RADIO_OPTION_TRAIN_START_DATE);
  const [boardingDate, setBoardingDate] = useState({} as DateDetails | undefined);
  const [boardingDaysOfRun, setBoardingDaysOfRun] = useState([] as (boolean | undefined)[]);

  // ----------
  const [upcomingJourneyDetails, setUpcomingJourneyDetails] = useState([] as UpcomingJourneyCardDetails[]);

  // ---------- effects ----------
  useEffect(() => {
    getRecentSearch();
  }, []);

  useEffect(() => {
    if (!trainDetail?.trainNumber && recentSearchTrains && recentSearchTrains.length > 0) {
      onTrainSelected(recentSearchTrains[0]);
    }
  }, [recentSearchTrains]);

  useEffect(() => {
    const dateList: any[] = getLTSDateListWithDaysOfRun(daysOfRun);
    const defaultDateIndex = PRIORITY_OF_DEFAULT_DATE_INDEX_ARRAY.reduce((acc, dateIndex) => {
      return dateList[dateIndex].valid ? dateIndex : acc;
    });
    setDateDetail(dateList[defaultDateIndex]);
  }, [daysOfRun]);

  useEffect(() => {
    setDaysOfRun([]);
    setTotalDaysOfRun(1);
    setStationsList([]);
    getMoreDetails(trainDetail?.trainNumber);
    setRadioValue(RADIO_OPTION_TRAIN_START_DATE);
  }, [trainDetail?.trainNumber]);

  // ----------
  useEffect(() => {
    if (radioValue === RADIO_OPTION_TRAIN_START_DATE) {
      setDateSectionData({
        clearance: true,
        startDateOptionSelected: true
      });
    } else {
      setDateSectionData({
        clearance: !!boardingStation?.stationCode || isEmpty(stationsList),
        startDateOptionSelected: false
      });
    }
  }, [radioValue, boardingStation?.stationCode]);

  useEffect(() => {
    if (!isEmpty(boardingStation?.stationCode)) {
      const station = stationsList.find((st) => st.code === boardingStation?.stationCode);
      const calculatedStartDate = getDateDetails(boardingDate?.date, 1 - (station?.day ?? 1));
      if (dateDetail?.date !== calculatedStartDate.date) setDateDetail(calculatedStartDate);
      const _boardingDaysOfRun = [false, false, false, false, false, false, false];
      if (isEmpty(daysOfRun)) setBoardingDaysOfRun(daysOfRun);
      else {
        daysOfRun.forEach((day, dayIndex) => {
          if (day) _boardingDaysOfRun[(dayIndex + 6 + (station?.day ?? 1)) % 7] = true;
        });
        setBoardingDaysOfRun(_boardingDaysOfRun);
      }
    }
  }, [boardingStation?.stationCode, boardingDate?.date]);

  useEffect(() => {
    if (!isEmpty(boardingStation?.stationCode)) {
      const station = stationsList.find((st) => st.code === boardingStation?.stationCode);
      const calculatedBoardingDate = getDateDetails(dateDetail?.date, (station?.day ?? 1) - 1);
      if (boardingDate?.date !== calculatedBoardingDate.date) setBoardingDate(calculatedBoardingDate);
    } else {
      if (dateDetail?.date !== boardingDate?.date) setBoardingDate(dateDetail);
    }
  }, [dateDetail?.date]);

  useEffect(() => {
    const _boardingDaysOfRun = [false, false, false, false, false, false, false];
    if (isEmpty(daysOfRun)) setBoardingDaysOfRun(daysOfRun);
    else {
      daysOfRun.forEach((day, dayIndex) => {
        if (day) {
          for (let i = 0; i < totalDaysOfRun; i++) _boardingDaysOfRun[(dayIndex + i) % 7] = true;
        }
      });
      setBoardingDaysOfRun(_boardingDaysOfRun);
    }
  }, [daysOfRun, totalDaysOfRun]);

  // ----------
  useEffect(() => {
    const updateUpcomingJourneyDetails = async () => {
      const response = await fetchUpcomingJourneysList();
      setUpcomingJourneyDetails(response);
    };

    updateUpcomingJourneyDetails();
  }, []);

  // ---------- functions ----------
  const getRecentSearch = async () => {
    const recentSearchTrainsVar = await getDataFromLocalStorage(ASYNC_STORAGE_KEYS.trainRecentSearchHistory);
    setRecentSearchTrains(recentSearchTrainsVar);
  };

  const onTrainSelected = (trainDetailVar: TrainDetail) => {
    if (trainDetailVar?.trainNumber === trainDetail?.trainNumber) {
      return;
    }
    const updatedTrainStatusDetail = {
      trainDetail: trainDetailVar,
      stationDetail: undefined,
      dateDetail: undefined
    };
    updateTrainStatus(updatedTrainStatusDetail);
  };

  const getMoreDetails = async (trainNumber: string) => {
    if (!trainNumber) return;
    const cachedData: MoreDetails = await getDataFromLocalStorage(ASYNC_STORAGE_KEYS.moreDetails + trainNumber);
    if (!isEmpty(cachedData)) {
      processMoreDetails(cachedData);
    }
    const moreDetails: MoreDetails = await fetchStationList(trainNumber);
    processMoreDetails(moreDetails);
    !isEmpty(moreDetails?.Stations) && setDataInLocalStorage(ASYNC_STORAGE_KEYS.moreDetails + trainNumber, moreDetails);
  };

  const processMoreDetails = (moreDetails: MoreDetails) => {
    const daysOfRun = isEmpty(moreDetails?.Train?.DaysOfRun)
      ? []
      : LABELS.WEEKDAYS.map((day) => moreDetails?.Train?.DaysOfRun.find((value) => value.WeekDay === day)?.Start);
    !isEmpty(daysOfRun) && setDaysOfRun(daysOfRun);
    moreDetails?.Train?.TotalDurationInDays !== undefined && setTotalDaysOfRun(moreDetails?.Train?.TotalDurationInDays);
    !isEmpty(moreDetails?.Stations) && setStationsList(moreDetails?.Stations);
  };

  const updateTrainStatus = (updatedTrainStatusDetail: TrainStatusDetail) => {
    setTrainDetail(updatedTrainStatusDetail?.trainDetail);
    setBoardingStation(updatedTrainStatusDetail.stationDetail);
    setDateDetail(updatedTrainStatusDetail.dateDetail);
  };

  // ---------- context value ----------
  const contextValue = {
    recentSearchTrains,
    trainDetail,
    boardingStation,
    dateDetail,
    daysOfRun,
    totalDaysOfRun,
    stationsList,
    dateSectionData,
    setRecentSearchTrains,
    setBoardingStation,
    setDateDetail,
    setDateSectionData,
    // ----------
    radioValue,
    boardingDate,
    boardingDaysOfRun,
    setRadioValue,
    setBoardingDate,
    // ----------
    upcomingJourneyDetails,
    // ----------
    onTrainSelected
  };

  // ---------- jsx ----------
  return <LTSContext.Provider value={contextValue}>{children}</LTSContext.Provider>;
};

export default LTSContextProvider;
